// src/pages/logistica/Caducidades.jsx
// Caducidades — Inventario PT (NetSuite customsearch425) con valor en riesgo:
// valor del lote = piezas × costo por pieza del último Excel de costos cargado.
// Los rangos del semáforo vienen del servidor (Costos PT → Semáforo).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api, { kpiService } from '../../services/api';

const ORDEN_CATEGORIAS = ['Granola', 'Cereal', 'Barras', 'Otro'];

const COLORES = {
  rojo: { color: '#B23A48', bg: '#FDF3F1' },
  amarillo: { color: '#B8862E', bg: '#FBF6E9' },
  verde: { color: '#3E7C5A', bg: '#F0F7F2' },
  vencido: { color: '#fff', bg: '#7A2333' },
};

const pesos = (v) => (v === null || v === undefined ? '—' : `$${Math.round(Number(v)).toLocaleString('es-MX')}`);
const pesosDec = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fecha = (d) => new Date(d).toLocaleDateString('es-MX');
const nombreMes = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : null);

export default function Caducidades() {
  const [lotes, setLotes] = useState([]);
  const [rangos, setRangos] = useState({ rojo_hasta: 95, amarillo_hasta: 120 });
  const [ultimoMesCosto, setUltimoMesCosto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [filtroSemaforo, setFiltroSemaforo] = useState('');

  async function cargar() {
    try {
      setCargando(true);
      const res = await api.get('/api/logistica/caducidades');
      setLotes(res.data.data);
      if (res.data.semaforo) setRangos(res.data.semaforo);
      setUltimoMesCosto(res.data.costos?.ultimo_mes || null);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  async function handleSincronizar() {
    try {
      setSincronizando(true);
      setMensaje(null);
      await kpiService.sync('caducidades-lotes');
      await cargar();
      setMensaje({ tipo: 'ok', texto: '✓ Sincronizado con NetSuite.' });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo sincronizar. Revisa la consola.' });
    } finally {
      setSincronizando(false);
    }
  }

  const semaforoInfo = {
    rojo: { ...COLORES.rojo, label: `Rojo (1–${rangos.rojo_hasta} días)` },
    amarillo: { ...COLORES.amarillo, label: `Amarillo (${rangos.rojo_hasta + 1}–${rangos.amarillo_hasta} días)` },
    verde: { ...COLORES.verde, label: `Verde (${rangos.amarillo_hasta + 1}+ días)` },
    vencido: { ...COLORES.vencido, label: 'Vencido' },
  };

  // Totales por semáforo (piezas, lotes y valor)
  const totales = useMemo(() => {
    const t = {};
    ['vencido', 'rojo', 'amarillo', 'verde'].forEach((k) => { t[k] = { lotes: 0, piezas: 0, valor: 0, sinCosto: 0 }; });
    lotes.forEach((l) => {
      const g = t[l.semaforo] || t.verde;
      g.lotes++;
      g.piezas += Number(l.piezas || 0);
      if (l.valor === null || l.valor === undefined) g.sinCosto++;
      else g.valor += Number(l.valor);
    });
    return t;
  }, [lotes]);
  const valorTotal = Object.values(totales).reduce((s, t) => s + t.valor, 0);
  const piezasTotal = lotes.reduce((s, l) => s + Number(l.piezas || 0), 0);
  const cajasTotal = lotes.reduce((s, l) => s + (l.cajas !== null && l.cajas !== undefined ? Number(l.cajas) : 0), 0);
  const lotesSinCajas = lotes.filter((l) => l.cajas === null || l.cajas === undefined).length;
  const lotesSinCosto = lotes.filter((l) => l.costo_pieza === null || l.costo_pieza === undefined);
  const articulosSinCosto = [...new Set(lotesSinCosto.map((l) => l.articulo))];

  function excel() {
    const filas = lotes.map((l) => ({
      Categoría: l.categoria, UPC: l.codigo_upc, Artículo: l.articulo, Lote: l.numero_lote,
      'Fecha caducidad': fecha(l.fecha_caducidad), 'Días': l.dias_caducidad, Semáforo: semaforoInfo[l.semaforo]?.label || l.semaforo,
      Piezas: Number(l.piezas), Cajas: l.cajas !== null ? Number(l.cajas) : '',
      'Costo por pieza': l.costo_pieza !== null ? Number(l.costo_pieza) : '', 'Mes del costo': nombreMes(l.costo_mes) || '',
      Valor: l.valor !== null ? Number(l.valor) : '', Ubicación: l.ubicacion || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), 'Caducidades');
    XLSX.writeFile(wb, `Caducidades_PT_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  if (cargando) return <div style={styles.contenedor}>Cargando...</div>;

  const visibles = filtroSemaforo ? lotes.filter((l) => l.semaforo === filtroSemaforo) : lotes;
  // Agrupar por categoría, respetando el orden fijo (las que no vengan simplemente no se muestran)
  const porCategoria = ORDEN_CATEGORIAS
    .map((cat) => ({ categoria: cat, filas: visibles.filter((l) => l.categoria === cat) }))
    .filter((g) => g.filas.length > 0);

  return (
    <div style={styles.contenedor} className="contenedor-captura">
      <div style={styles.encabezado}>
        <div>
          <h1 style={styles.titulo}>Caducidades — Inventario PT</h1>
          <p style={styles.subtitulo}>
            Almacén PT Belenes · fuente: NetSuite (customsearch425)
            {ultimoMesCosto ? ` · costos de ${nombreMes(ultimoMesCosto)}` : ' · sin costos cargados'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/logistica/costos-pt" style={{ ...styles.botonSecundario, textDecoration: 'none' }}>Costos y semáforo</Link>
          <button onClick={excel} disabled={!lotes.length} style={styles.botonSecundario}>⬇ Excel</button>
          <button onClick={handleSincronizar} disabled={sincronizando} style={styles.botonSync}>
            {sincronizando ? 'Sincronizando...' : 'Sincronizar ahora'}
          </button>
        </div>
      </div>

      {mensaje && <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      {/* Valor en riesgo por semáforo */}
      <div style={styles.tarjetas}>
        {['rojo', 'amarillo', 'vencido', 'verde'].map((k) => {
          const t = totales[k];
          const info = semaforoInfo[k];
          const activo = filtroSemaforo === k;
          return (
            <button key={k} onClick={() => setFiltroSemaforo(activo ? '' : k)}
              style={{ ...styles.tarjetaValor, borderTop: `3px solid ${k === 'vencido' ? COLORES.vencido.bg : info.color}`, outline: activo ? `2px solid ${k === 'vencido' ? COLORES.vencido.bg : info.color}` : 'none' }}>
              <div style={styles.tarjetaLabel}>{info.label}</div>
              <div style={{ ...styles.tarjetaMonto, color: k === 'vencido' ? COLORES.vencido.bg : info.color }}>{pesos(t.valor)}</div>
              <div style={styles.tarjetaSub}>
                {t.lotes} lotes · {Math.round(t.piezas).toLocaleString('es-MX')} piezas
                {valorTotal > 0 && ` · ${((t.valor / valorTotal) * 100).toFixed(1)}%`}
              </div>
              {t.sinCosto > 0 && <div style={{ ...styles.tarjetaSub, color: '#B8862E' }}>{t.sinCosto} lote(s) sin costo</div>}
            </button>
          );
        })}
        <div style={{ ...styles.tarjetaValor, borderTop: '3px solid #4F7CA8', cursor: 'default' }}>
          <div style={styles.tarjetaLabel}>Piezas en inventario</div>
          <div style={{ ...styles.tarjetaMonto, color: '#4F7CA8' }}>{Math.round(piezasTotal).toLocaleString('es-MX')}</div>
          <div style={styles.tarjetaSub}>
            {lotesSinCajas ? '≥ ' : ''}{Math.round(cajasTotal).toLocaleString('es-MX')} cajas · {lotes.length} lotes
          </div>
          {lotesSinCajas > 0 && <div style={{ ...styles.tarjetaSub, color: '#B8862E' }}>{lotesSinCajas} lote(s) sin conversión a cajas</div>}
        </div>
        <div style={{ ...styles.tarjetaValor, borderTop: '3px solid #4A1E5C', cursor: 'default' }}>
          <div style={styles.tarjetaLabel}>Valor total del inventario</div>
          <div style={{ ...styles.tarjetaMonto, color: '#4A1E5C' }}>{pesos(valorTotal)}</div>
          <div style={styles.tarjetaSub}>{lotes.length} lotes</div>
        </div>
      </div>
      {filtroSemaforo && (
        <div style={styles.filtroActivo}>
          Mostrando solo: <strong>{semaforoInfo[filtroSemaforo].label}</strong>{' '}
          <button onClick={() => setFiltroSemaforo('')} style={styles.linkBoton}>ver todos</button>
        </div>
      )}

      {articulosSinCosto.length > 0 && (
        <div style={styles.avisoCosto}>
          {articulosSinCosto.length} artículo(s) sin costo por pieza ({lotesSinCosto.length} lotes): su valor no está sumado.{' '}
          {articulosSinCosto.slice(0, 4).join(', ')}{articulosSinCosto.length > 4 ? '…' : ''}.{' '}
          <Link to="/logistica/costos-pt" style={styles.link}>Cargar costos o ligar artículos</Link>
        </div>
      )}

      <div style={styles.leyenda}>
        {Object.entries(semaforoInfo).map(([clave, info]) => (
          <span key={clave} style={{ ...styles.chipLeyenda, background: info.bg, color: info.color }}>
            <span style={{ ...styles.puntoLeyenda, background: info.color }} /> {info.label}
          </span>
        ))}
      </div>

      {porCategoria.length === 0 && <div style={styles.tarjeta}>{lotes.length ? 'No hay lotes con este filtro.' : 'Sin lotes registrados. Corre la sincronización.'}</div>}

      {porCategoria.map(({ categoria, filas }) => {
        const totalPiezas = filas.reduce((s, f) => s + parseFloat(f.piezas || 0), 0);
        const totalCajas = filas.reduce((s, f) => s + (f.cajas !== null ? parseFloat(f.cajas) : 0), 0);
        const totalValor = filas.reduce((s, f) => s + (f.valor !== null ? parseFloat(f.valor) : 0), 0);
        const hayCajasFaltantes = filas.some((f) => f.cajas === null);
        const hayValorFaltante = filas.some((f) => f.valor === null);

        return (
          <div key={categoria} style={styles.tarjeta}>
            <h2 style={styles.tituloCategoria}>
              {categoria} <span style={styles.conteo}>({filas.length} lotes · {pesos(totalValor)})</span>
            </h2>
            <div className="tabla-scroll">
              <table style={styles.tabla}>
                <thead>
                  <tr>
                    <th style={styles.th}>UPC</th>
                    <th style={styles.th}>Artículo</th>
                    <th style={styles.th}>Lote</th>
                    <th style={styles.th}>Fecha caduc.</th>
                    <th style={styles.thNum}>Piezas</th>
                    <th style={styles.thNum}>Cajas</th>
                    <th style={styles.thNum}>Días</th>
                    <th style={styles.thNum}>Costo/pza</th>
                    <th style={styles.thNum}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => {
                    const info = semaforoInfo[f.semaforo] || semaforoInfo.verde;
                    const sinCosto = f.costo_pieza === null || f.costo_pieza === undefined;
                    return (
                      <tr key={`${f.codigo_upc}-${f.numero_lote}`} style={{ background: f.semaforo === 'vencido' ? '#F6E3E6' : info.bg }}>
                        <td style={styles.td}>{f.codigo_upc}</td>
                        <td style={styles.td}>{f.articulo}</td>
                        <td style={styles.td}>{f.numero_lote}</td>
                        <td style={styles.td}>{fecha(f.fecha_caducidad)}</td>
                        <td style={styles.tdNum}>{Number(f.piezas).toLocaleString('es-MX')}</td>
                        <td style={styles.tdNum}>{f.cajas !== null ? Number(f.cajas).toLocaleString('es-MX') : '—'}</td>
                        <td style={{ ...styles.tdNum, color: f.semaforo === 'vencido' ? '#7A2333' : info.color, fontWeight: 700 }}>{f.dias_caducidad}</td>
                        <td style={{ ...styles.tdNum, color: sinCosto ? '#B8862E' : undefined }} title={f.costo_producto ? `${f.costo_producto} · ${nombreMes(f.costo_mes)}` : 'Sin costo cargado'}>
                          {sinCosto ? 'sin costo' : pesosDec(f.costo_pieza)}
                        </td>
                        <td style={{ ...styles.tdNum, fontWeight: 600 }}>{sinCosto ? '—' : pesos(f.valor)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={styles.filaTotal}>
                    <td style={styles.td} colSpan={4}>TOTAL {categoria}</td>
                    <td style={styles.tdNum}>{totalPiezas.toLocaleString('es-MX')}</td>
                    <td style={styles.tdNum}>{hayCajasFaltantes ? '≥ ' : ''}{totalCajas.toLocaleString('es-MX')}</td>
                    <td style={styles.tdNum}></td>
                    <td style={styles.tdNum}></td>
                    <td style={styles.tdNum}>{hayValorFaltante ? '≥ ' : ''}{pesos(totalValor)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {hayCajasFaltantes && (
              <div style={styles.avisoCajas}>
                Algunas cajas no se pudieron calcular (falta conversión) — el total de Cajas está incompleto. Ver{' '}
                <a href="/logistica/conversion-cajas" style={styles.link}>Conversión de Cajas</a>.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '1120px' },
  encabezado: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexWrap: 'wrap', gap: '10px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '2px' },
  subtitulo: { fontSize: '12px', color: '#8A7690' },
  botonSync: { padding: '9px 16px', fontSize: '13px', fontWeight: 600, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  botonSecundario: { padding: '9px 14px', fontSize: '12.5px', fontWeight: 600, color: '#4A1E5C', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
  mensaje: { fontSize: '13px', margin: '10px 0', fontWeight: 600 },
  tarjetas: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', margin: '16px 0 8px' },
  tarjetaValor: { textAlign: 'left', background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', cursor: 'pointer', fontFamily: 'Inter, sans-serif' },
  tarjetaLabel: { fontSize: '11px', fontWeight: 600, color: '#8A7690', textTransform: 'uppercase', letterSpacing: '0.4px' },
  tarjetaMonto: { fontSize: '24px', fontWeight: 700, margin: '6px 0 2px' },
  tarjetaSub: { fontSize: '11.5px', color: '#8A7690' },
  filtroActivo: { fontSize: '12px', color: '#6E4F80', margin: '4px 0 8px' },
  linkBoton: { background: 'none', border: 'none', color: '#6A2C75', textDecoration: 'underline', cursor: 'pointer', fontSize: '12px', padding: 0 },
  avisoCosto: { fontSize: '12px', color: '#7a5417', background: '#FBF6E9', border: '1px solid #EAD9AE', borderRadius: '8px', padding: '8px 12px', margin: '8px 0' },
  leyenda: { display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '14px 0 20px' },
  chipLeyenda: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, padding: '5px 10px', borderRadius: '20px' },
  puntoLeyenda: { width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block' },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', marginBottom: '20px' },
  tituloCategoria: { fontSize: '16px', fontWeight: 700, color: '#2A1830', marginBottom: '12px' },
  conteo: { fontSize: '12px', fontWeight: 400, color: '#8A7690' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th: { textAlign: 'left', padding: '8px 10px', color: '#8A7690', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E6DDD2' },
  thNum: { textAlign: 'right', padding: '8px 10px', color: '#8A7690', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E6DDD2' },
  td: { padding: '7px 10px', borderBottom: '1px solid #F0E9E0' },
  tdNum: { padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #F0E9E0' },
  filaTotal: { fontWeight: 700, background: '#F5F3F0' },
  avisoCajas: { marginTop: '10px', fontSize: '11.5px', color: '#B8862E' },
  link: { color: '#6A2C75', fontWeight: 600 },
};